import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
  Paper,
  Divider,
  Badge,
  Stack,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  DirectionsCar as CarIcon,
  Star as StarIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  LocalOffer as PriceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useBooking } from '../contexts/BookingContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiService } from '../services/api.js';

const AppointmentPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  
  const { 
    selectedGarage,
    vehicleRegistration,
    vehicleData,
    postcode,
    selectedSlot,
    availableSlots,
    selectedProducts,
    selectedAddons,
    totalAmount,
    setAvailableSlots,
    selectSlot,
    updatePricing,
    setGarageSelectedProducts,
    setGarageSelectedAddons,
    setLoading,
    setError,
    clearError,
    isLoading,
    error 
  } = useBooking();

  // State for calendar and slots
  const [weekDates, setWeekDates] = useState([]);
  const [weeklySlots, setWeeklySlots] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [apiSelectedProducts, setApiSelectedProducts] = useState([]);
  const [apiSelectedAddons, setApiSelectedAddons] = useState([]);

  // Redirect if no required data
  useEffect(() => {
    if (!vehicleRegistration || !selectedGarage) {
      navigate('/');
      return;
    }
    if (!selectedProducts || selectedProducts.length === 0) {
      navigate('/products');
      return;
    }
  }, [selectedGarage, vehicleRegistration, selectedProducts, navigate]);

  // Generate dates from current date to 2 weeks ahead
  const generateWeekDates = useCallback(() => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate 14 days starting from today
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    
    setWeekDates(dates);
  }, []);

  // Load availability for week dates
  useEffect(() => {
    if (!selectedGarage?.id || !hasLoadedInitial) return;

    const loadWeekAvailability = async () => {
      if (loadingSlots) return;
      
      setLoadingSlots(true);
      clearError();

      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Start date: today
        const startDate = new Date(today);
        
        // End date: 2 weeks from today
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 13); // 13 days ahead (14 total days)
        
        // Format dates for API (ensure correct format)
        const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
        const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

        // Include selected services for duration calculation
        const productIds = selectedProducts.map(p => p.product_id);
        const addonIds = selectedAddons.map(a => a.addon_id);
        
        const response = await apiService.post(
          `/garages/${selectedGarage.id}/availability`,
          {
            start_date: startDateStr,
            end_date: endDateStr,
            product_ids: productIds,
            addon_ids: addonIds
          }
        );

        const slotsData = response.data;
        
        // Organize slots by date
        const slotsByDate = {};
        if (slotsData.slots) {
          slotsData.slots.forEach(daySlot => {
            // Convert date to consistent local format (YYYY-MM-DD)
            const slotDate = new Date(daySlot.date);
            const dateKey = `${slotDate.getFullYear()}-${String(slotDate.getMonth() + 1).padStart(2, '0')}-${String(slotDate.getDate()).padStart(2, '0')}`;
            slotsByDate[dateKey] = daySlot;
          });
        }

        setWeeklySlots(slotsByDate);
        setAvailableSlots(slotsData.slots || []);
        
        // Update garage-specific products and addons from API response
        const apiProducts = slotsData.selected_products || [];
        const apiAddons = slotsData.selected_addons || [];
        
        // Update local state for this page
        setApiSelectedProducts(apiProducts);
        setApiSelectedAddons(apiAddons);
        
        // Update booking context with garage-specific data
        setGarageSelectedProducts(apiProducts);
        setGarageSelectedAddons(apiAddons);

        // Auto-select first available slot for today if not already selected
        if (!selectedSlot?.time && slotsData.slots) {
          const today = new Date().toDateString();
          const todaySlots = slotsData.slots.find(daySlot => 
            new Date(daySlot.date).toDateString() === today
          );
          
          if (todaySlots && todaySlots.time_slots) {
            const firstAvailableSlot = todaySlots.time_slots.find(slot => slot.is_available);
            
            if (firstAvailableSlot) {
              // Calculate total for auto-selected slot using garage-specific pricing
              let autoSubtotal = 0;
              apiProducts.forEach(product => {
                autoSubtotal += parseFloat(product.garage_price || product.base_price || 0);
              });
              apiAddons.forEach(addon => {
                if (!addon.is_free) {
                  autoSubtotal += parseFloat(addon.garage_price || addon.base_price || 0);
                }
              });
              
              const autoMultiplier = firstAvailableSlot.price_multiplier || 1.0;
              const autoTotalAmount = autoSubtotal * autoMultiplier;
              
              selectSlot({
                date: today,
                time: firstAvailableSlot.time,
                slot_id: firstAvailableSlot.slot_id,
                price: firstAvailableSlot.price,
                price_multiplier: firstAvailableSlot.price_multiplier,
                calculatedAmount: autoTotalAmount
              });
            }
          }
        }

      } catch (error) {
        setError('Failed to load appointment availability');
      } finally {
        setLoadingSlots(false);
      }
    };

    if (hasLoadedInitial) {
      loadWeekAvailability();
    }
  }, [selectedGarage?.id, hasLoadedInitial]);

  // Initialize dates and auto-select today when component mounts
  useEffect(() => {
    if (selectedGarage?.id && !hasLoadedInitial) {
      generateWeekDates();
      // Auto-select today's date
      const today = new Date();
      selectSlot({
        date: today.toDateString(),
        time: null,
        slot_id: null,
        price: null,
        price_multiplier: null,
        calculatedAmount: null
      }); // Initialize with date only
      setHasLoadedInitial(true);
    }
  }, [selectedGarage?.id, hasLoadedInitial, selectSlot]);

  // Update dates when initialized
  useEffect(() => {
    if (hasLoadedInitial) {
      generateWeekDates();
    }
  }, [hasLoadedInitial, generateWeekDates]);


  // Handle time slot selection
  const handleTimeSlotSelect = (date, timeSlot) => {
    if (!timeSlot.is_available) return;
    
    // Calculate total for selected products and addons using garage-specific pricing
    let subtotal = 0;
    apiSelectedProducts.forEach(product => {
      subtotal += parseFloat(product.garage_price || product.base_price || 0);
    });
    apiSelectedAddons.forEach(addon => {
      if (!addon.is_free) {
        subtotal += parseFloat(addon.garage_price || addon.base_price || 0);
      }
    });
    
    // Apply slot multiplier (weekend, urgent booking, etc.)
    const multiplier = timeSlot.price_multiplier || 1.0;
    const totalAmount = subtotal * multiplier;
    
    selectSlot({
      date: date.toDateString(),
      time: timeSlot.time,
      slot_id: timeSlot.slot_id,
      price: subtotal,
      price_multiplier: timeSlot.price_multiplier,
      calculatedAmount: totalAmount,
      subtotal: subtotal
    });
    
    // Update booking context pricing
    updatePricing({
      subtotal: subtotal,
      discountAmount: 0,
      totalAmount: totalAmount
    });
  };



  // Handle continue to checkout (Order Summary)
  const handleContinue = () => {
    if (!selectedSlot || !selectedSlot.time || !selectedSlot.slot_id || selectedSlot.calculatedAmount === null) {
      return;
    }
    
    // Go directly to order summary page (no auth check yet)
    navigate('/summary');
  };


  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          component="button" 
          variant="body2" 
          onClick={() => navigate('/')}
          sx={{ textDecoration: 'none' }}
        >
          Home
        </Link>
        <Link 
          component="button" 
          variant="body2" 
          onClick={() => navigate('/garages')}
          sx={{ textDecoration: 'none' }}
        >
          Find Garages
        </Link>
        <Typography variant="body2" color="text.primary">
          Book Appointment
        </Typography>
      </Breadcrumbs>

      {/* Vehicle Details Section - Full width responsive */}
      <Card elevation={2} sx={{ mb: 3, width: '100%' }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          {/* Registration and Not your vehicle link - same line */}
          <Box sx={{ mb: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'nowrap' }}>
              <CarIcon color="primary" sx={{ fontSize: { xs: 24, sm: 28 }, flexShrink: 0 }} />
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, flexShrink: 0 }}>
                {vehicleRegistration}
              </Typography>
              
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  clearError();
                  navigate('/', { 
                    state: { 
                      vehicleReg: vehicleRegistration,
                      postcode: postcode 
                    }
                  });
                }}
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  p: 0,
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ml: 1,
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'transparent'
                  }
                }}
              >
                Not your vehicle?
              </Button>
            </Box>
          </Box>
          
          {/* Vehicle details and MOT expiry - responsive flex wrap */}
          {vehicleData && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flexWrap: 'wrap',
              width: '100%'
            }}>
              <Typography variant="body2" color="text.secondary">
                {vehicleData.make}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vehicleData.year}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vehicleData.fuel_type}
              </Typography>
              
              {/* MOT Expiry Chip - can wrap to next line */}
              {vehicleData?.mot_expiry_date && (
                <Chip 
                  label={`MOT expires on ${new Date(vehicleData.mot_expiry_date).toLocaleDateString('en-GB')}`}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ 
                    fontWeight: 'medium',
                    fontSize: '0.875rem',
                    flexShrink: 0
                  }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Garage Details Section - Same style as GaragePage */}
      {selectedGarage && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  {selectedGarage.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2">
                      {selectedGarage.address}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedGarage.postcode}
                    </Typography>
                  </Box>
                </Box>

                {selectedGarage.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {selectedGarage.phone}
                    </Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Rating 
                      value={selectedGarage.rating || 4.2} 
                      readOnly 
                      size="small" 
                      precision={0.1}
                    />
                    <Typography variant="body2" color="text.secondary">
                      ({selectedGarage.total_reviews || 0} reviews)
                    </Typography>
                  </Box>
                  
                  {selectedGarage.distance_miles && (
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      {selectedGarage.distance_miles} miles away
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            {selectedGarage.facilities && selectedGarage.facilities.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedGarage.facilities.map((facility, index) => (
                    <Chip 
                      key={index} 
                      label={facility} 
                      size="small" 
                      variant="outlined" 
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected Services Section */}
      {(apiSelectedProducts.length > 0 || apiSelectedAddons.length > 0) && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Selected Services
            </Typography>
            
            {/* Products */}
            {apiSelectedProducts.length > 0 && (
              <Box sx={{ mb: apiSelectedAddons.length > 0 ? 2 : 0 }}>
                <Stack spacing={1}>
                  {apiSelectedProducts.map((product) => (
                    <Box 
                      key={product.id} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          £{product.garage_price.toFixed(2)} • {product.duration_minutes} mins
                        </Typography>
                      </Box>
                      <Chip 
                        label={product.category} 
                        size="small" 
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            
            {/* Addons */}
            {apiSelectedAddons.length > 0 && (
              <Box>
                <Stack spacing={1}>
                  {apiSelectedAddons.map((addon) => (
                    <Box 
                      key={addon.id} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {addon.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {addon.garage_price === 0 ? 'Free' : `£${addon.garage_price.toFixed(2)}`} • {addon.duration_minutes} mins
                        </Typography>
                      </Box>
                      <Chip 
                        label={addon.category} 
                        size="small" 
                        variant="outlined"
                        color="secondary"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Appointment Date & Time Section */}
      <Card elevation={2}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
            Appointment Date & Time
          </Typography>


          {/* Professional Calendar with Inline Slots */}
          {loadingSlots ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ 
              width: '100%',
              overflowX: 'auto',
              mb: 3,
              pb: 2,
              pt: 2,
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'primary.main',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }
            }}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                minWidth: 'fit-content',
                px: 2,
                alignItems: 'stretch'
              }}>
                {weekDates.map((date, index) => {
                  // Use local date format to avoid timezone issues
                  const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const daySlots = weeklySlots[dateKey];
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dateToCompare = new Date(date);
                  dateToCompare.setHours(0, 0, 0, 0);
                  const isPast = dateToCompare < today;
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = selectedSlot?.date === date.toDateString();
                  const hasSlots = daySlots && daySlots.time_slots && daySlots.time_slots.length > 0;
                  const availableSlots = hasSlots ? daySlots.time_slots.filter(slot => slot.is_available) : [];
                  
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: { xs: 140, sm: 160, md: 180 },
                        maxWidth: { xs: 140, sm: 160, md: 180 },
                      }}
                    >
                      {/* Date Header */}
                      <Paper 
                        variant="outlined"
                        elevation={0}
                        sx={{ 
                          p: { xs: 1.5, sm: 2 }, 
                          textAlign: 'center',
                          position: 'relative',
                          border: '2px solid',
                          borderColor: isSelected
                            ? 'primary.main' 
                            : hasSlots 
                              ? 'success.light' 
                              : 'divider',
                          backgroundColor: isSelected
                            ? 'primary.50' 
                            : isPast 
                              ? 'action.hover' 
                              : hasSlots 
                                ? 'success.50' 
                                : 'background.paper',
                          opacity: isPast ? 0.6 : 1,
                          pointerEvents: loadingSlots ? 'none' : 'auto',
                          ...(isSelected && {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}25`,
                          }),
                        }}
                      >
                        {/* Month Display - positioned outside above date card like admin calendar */}
                        {(date.getDate() === 1 || isToday) && (
                          <Typography 
                            variant="caption" 
                            fontWeight="700"
                            color="primary.main"
                            sx={{ 
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              textTransform: 'uppercase',
                              letterSpacing: 1,
                              display: 'block',
                              position: 'absolute',
                              top: -20,
                              left: 0,
                              right: 0,
                              textAlign: 'center',
                              zIndex: 1
                            }}
                          >
                            {date.toLocaleDateString('en-GB', { month: 'short' })}
                          </Typography>
                        )}
                        
                        <Typography 
                          variant="caption" 
                          fontWeight="600" 
                          color={isSelected
                            ? 'primary.main' 
                            : isPast 
                              ? 'text.disabled' 
                              : 'text.secondary'}
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            display: 'block',
                            mb: 0.5
                          }}
                        >
                          {isToday ? 'TODAY' : date.toLocaleDateString('en-GB', { weekday: 'short' })}
                        </Typography>
                        <Typography 
                          variant="h5" 
                          fontWeight="700"
                          color={isSelected
                            ? 'primary.main' 
                            : isPast 
                              ? 'text.disabled' 
                              : 'text.primary'}
                          sx={{ 
                            fontSize: { xs: '1.5rem', sm: '1.75rem' },
                            lineHeight: 1,
                            mb: 1
                          }}
                        >
                          {date.getDate()}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color={isSelected
                            ? 'primary.main' 
                            : hasSlots 
                              ? "success.main" 
                              : "text.disabled"}
                          sx={{ 
                            fontSize: { xs: '0.6rem', sm: '0.65rem' },
                            fontWeight: 'medium',
                            display: 'block'
                          }}
                        >
                          {availableSlots.length} slots
                        </Typography>
                      </Paper>

                      {/* Time Slots Column */}
                      <Box sx={{ 
                        mt: 1, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 0.5
                      }}>
                        {!hasSlots ? (
                          <Paper sx={{ 
                            p: 2, 
                            textAlign: 'center',
                            backgroundColor: 'action.hover',
                            border: '1px dashed',
                            borderColor: 'divider'
                          }}>
                            <Typography variant="caption" color="text.secondary">
                              No slots available
                            </Typography>
                          </Paper>
                        ) : (
                          availableSlots.map((timeSlot, slotIndex) => {
                            const isSlotSelected = selectedSlot?.time === timeSlot.time && isSelected;
                            
                            return (
                                <Paper
                                  key={slotIndex}
                                  variant="outlined"
                                  elevation={0}
                                sx={{
                                  p: { xs: 1.5, sm: 2 },
                                  cursor: 'pointer',
                                  border: '1px solid',
                                  borderColor: isSlotSelected ? 'primary.main' : 'divider',
                                  backgroundColor: isSlotSelected ? 'primary.main' : 'background.paper',
                                  borderRadius: 1,
                                  pointerEvents: loadingSlots ? 'none' : 'auto'
                                }}
                                onClick={(e) => {
                                  if (loadingSlots) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleTimeSlotSelect(date, timeSlot);
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={isSlotSelected ? 'white' : 'text.primary'}
                                  sx={{ 
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    textAlign: 'center',
                                    mb: 1
                                  }}
                                >
                                  {new Date(`2000-01-01T${timeSlot.time}`).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </Typography>
                                <Typography 
                                  variant="caption"
                                  color={isSlotSelected ? 'white' : 'text.secondary'}
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    fontWeight: '500',
                                    textAlign: 'center',
                                    display: 'block'
                                  }}
                                >
                                  £{(
                                    (
                                      (apiSelectedProducts.reduce((sum, p) => sum + p.garage_price, 0)) + 
                                      (apiSelectedAddons.reduce((sum, a) => sum + a.garage_price, 0))
                                    ) * (timeSlot.price_multiplier || 1.0)
                                  ).toFixed(2)}
                                  {timeSlot.price_multiplier && timeSlot.price_multiplier > 1.0 && (
                                    <Box component="span" sx={{ 
                                      fontSize: '0.65rem', 
                                      opacity: 0.8,
                                      display: 'block'
                                    }}>
                                      ({(timeSlot.price_multiplier * 100).toFixed(0)}% surcharge)
                                    </Box>
                                  )}
                                </Typography>
                              </Paper>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}


          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}


          {/* Continue Button - Responsive */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mt: 4 
          }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              disabled={!selectedSlot?.date || !selectedSlot?.time}
              sx={{ 
                px: { xs: 3, sm: 4 },
                py: { xs: 1, sm: 1.5 },
                minWidth: { xs: '100%', sm: 200 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                maxWidth: { xs: '100%', sm: 'none' },
                '&.Mui-disabled': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  opacity: 0.8
                }
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : ' Continue to checkout'}
             
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AppointmentPage;